import { useFrame } from '@react-three/fiber'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useRef } from 'react'
import {
    AdditiveBlending,
    BufferAttribute,
    BufferGeometry,
    CanvasTexture,
    Color,
    DoubleSide,
    Group,
    Mesh,
    MeshBasicMaterial,
    Points,
    PointsMaterial,
    Vector3,
} from 'three'
import { END_CARD_SPARK_BURST_EVENT } from './endCardSparkBurstEvent'
import type { EndCardBackgroundVariant } from './endCardMaterials'
import { vibrate } from '@/src/lib/vibration'
import {
    endCardSparkBurstDelay,
    endCardSparkCount,
    endCardSparkDrag,
    endCardSparkEmitter1X,
    endCardSparkEmitter1Y,
    endCardSparkEmitter1Z,
    endCardSparkEmitter2X,
    endCardSparkEmitter2Y,
    endCardSparkEmitter2Z,
    endCardSparkEmitter3X,
    endCardSparkEmitter3Y,
    endCardSparkEmitter3Z,
    endCardSparkEmitter4X,
    endCardSparkEmitter4Y,
    endCardSparkEmitter4Z,
    endCardSparkEmitter5X,
    endCardSparkEmitter5Y,
    endCardSparkEmitter5Z,
    endCardSparkEndSize,
    endCardSparkLifetime,
    endCardSparkOpacity,
    endCardSparkOutwardVelocity,
    endCardSparkPositionJitter,
    endCardSparkRandomVelocity,
    endCardSparkShowEmitterDebug,
    endCardSparkSize,
    endCardSparkSpeed,
} from '@/src/game/config/constants'

type ActiveBurst = {
    age: number
    lifetime: number
    geometry: BufferGeometry
    material: PointsMaterial
    points: Points
    trailGeometry: BufferGeometry
    trailMaterial: MeshBasicMaterial
    trailMesh: Mesh
    positions: Float32Array
    tailPositions: Float32Array
    trailVertexPositions: Float32Array
    velocities: Float32Array
}

type EmitterPosition = {
    x: number
    y: number
    z: number
}

const preBurstDelaySeconds = 0.7
const sparkGravity = 1
const trailFollowStrength = 0.12
const trailLengthScale = 0.2
const trailWidthScale = 1
const _spawnPosition = new Vector3()
const _velocity = new Vector3()
const _targetColor = new Color()
const endCardSparkColors: Record<EndCardBackgroundVariant, string> = {
    battery: '#00ff0d',
    coin: '#ffae00',
    shield: '#0251fa',
    stars: '#e100ff',
}
const _hotColor = new Color('#ffffff')
const _currentColor = new Color()
const _trailColor = new Color()
const _head = new Vector3()
const _tail = new Vector3()
const _dir = new Vector3()
const _toCamera = new Vector3()
const _side = new Vector3()

function createCircleTexture() {
    const canvas = document.createElement('canvas')
    canvas.width = 128
    canvas.height = 128

    const context = canvas.getContext('2d')
    if (!context) {
        return null
    }

    const gradient = context.createRadialGradient(64, 64, 0, 64, 64, 64)
    gradient.addColorStop(0, 'rgba(255,255,255,1)')
    gradient.addColorStop(0.45, 'rgba(255,255,255,1)')
    gradient.addColorStop(0.85, 'rgba(255,255,255,0.35)')
    gradient.addColorStop(1, 'rgba(255,255,255,0)')

    context.clearRect(0, 0, 128, 128)
    context.fillStyle = gradient
    context.beginPath()
    context.arc(64, 64, 64, 0, Math.PI * 2)
    context.fill()

    const texture = new CanvasTexture(canvas)
    texture.needsUpdate = true
    return texture
}

function createRibbonTexture() {
    const canvas = document.createElement('canvas')
    canvas.width = 32
    canvas.height = 256

    const context = canvas.getContext('2d')
    if (!context) {
        return null
    }

    const gradient = context.createLinearGradient(0, 0, 0, 256)
    gradient.addColorStop(0, 'rgba(255,255,255,1)')
    gradient.addColorStop(0.2, 'rgba(255,255,255,0.95)')
    gradient.addColorStop(0.75, 'rgba(255,255,255,0.25)')
    gradient.addColorStop(1, 'rgba(255,255,255,0)')

    context.clearRect(0, 0, 32, 256)
    context.fillStyle = gradient
    context.fillRect(0, 0, 32, 256)

    context.globalCompositeOperation = 'destination-in'
    const edgeGradient = context.createLinearGradient(0, 0, 32, 0)
    edgeGradient.addColorStop(0, 'rgba(255,255,255,0)')
    edgeGradient.addColorStop(0.3, 'rgba(255,255,255,1)')
    edgeGradient.addColorStop(0.7, 'rgba(255,255,255,1)')
    edgeGradient.addColorStop(1, 'rgba(255,255,255,0)')
    context.fillStyle = edgeGradient
    context.fillRect(0, 0, 32, 256)

    const texture = new CanvasTexture(canvas)
    texture.needsUpdate = true
    return texture
}

function getEmitterPositions(): EmitterPosition[] {
    return [
        { x: endCardSparkEmitter1X, y: endCardSparkEmitter1Y, z: endCardSparkEmitter1Z },
        { x: endCardSparkEmitter2X, y: endCardSparkEmitter2Y, z: endCardSparkEmitter2Z },
        { x: endCardSparkEmitter3X, y: endCardSparkEmitter3Y, z: endCardSparkEmitter3Z },
        { x: endCardSparkEmitter4X, y: endCardSparkEmitter4Y, z: endCardSparkEmitter4Z },
        { x: endCardSparkEmitter5X, y: endCardSparkEmitter5Y, z: endCardSparkEmitter5Z },
    ]
}

type EndCardSparkBurstEffectProps = {
    variant: EndCardBackgroundVariant
}

export const EndCardSparkBurstEffect = observer(function EndCardSparkBurstEffect({
    variant,
}: EndCardSparkBurstEffectProps) {
    const rootRef = useRef<Group | null>(null)
    const burstsRef = useRef<ActiveBurst[]>([])
    const scheduledBurstsRef = useRef<number[]>([])
    const circleTexture = useMemo(() => createCircleTexture(), [])
    const ribbonTexture = useMemo(() => createRibbonTexture(), [])
    const burstColor = endCardSparkColors[variant]

    useEffect(() => {
        const clearScheduledBursts = () => {
            for (const timerId of scheduledBurstsRef.current) {
                window.clearTimeout(timerId)
            }
            scheduledBurstsRef.current = []
        }

        const triggerSequence = () => {
            clearScheduledBursts()
            getEmitterPositions().forEach((emitter, index) => {
                const timerId = window.setTimeout(() => {
                    spawnBurst(emitter)
                }, (preBurstDelaySeconds + index * endCardSparkBurstDelay) * 1000)
                scheduledBurstsRef.current.push(timerId)
            })
        }

        window.addEventListener(END_CARD_SPARK_BURST_EVENT, triggerSequence)
        return () => {
            window.removeEventListener(END_CARD_SPARK_BURST_EVENT, triggerSequence)
            clearScheduledBursts()
            for (const burst of burstsRef.current) {
                burst.points.removeFromParent()
                burst.trailMesh.removeFromParent()
                burst.geometry.dispose()
                burst.trailGeometry.dispose()
                burst.material.dispose()
                burst.trailMaterial.dispose()
            }
            burstsRef.current = []
            circleTexture?.dispose()
            ribbonTexture?.dispose()
        }
    }, [circleTexture, ribbonTexture])

    function spawnBurst(emitter: EmitterPosition) {
        const count = Math.max(1, Math.floor(endCardSparkCount))
        const burstLifetime = Math.max(endCardSparkLifetime, 0.0001)
        const positions = new Float32Array(count * 3)
        const tailPositions = new Float32Array(count * 3)
        const trailVertexPositions = new Float32Array(count * 12)
        const trailUvs = new Float32Array(count * 8)
        const velocities = new Float32Array(count * 3)
        const geometry = new BufferGeometry()
        const trailGeometry = new BufferGeometry()
        const indices: number[] = []
        geometry.setAttribute('position', new BufferAttribute(positions, 3))
        trailGeometry.setAttribute('position', new BufferAttribute(trailVertexPositions, 3))

        for (let index = 0; index < count; index += 1) {
            const i3 = index * 3
            const i4 = index * 4
            const random = endCardSparkRandomVelocity
            const jitter = endCardSparkPositionJitter

            _spawnPosition.set(
                emitter.x + (Math.random() * 2 - 1) * jitter,
                emitter.y + (Math.random() * 2 - 1) * jitter,
                emitter.z + (Math.random() * 2 - 1) * jitter * 0.5,
            )

            positions[i3] = _spawnPosition.x
            positions[i3 + 1] = _spawnPosition.y
            positions[i3 + 2] = _spawnPosition.z
            tailPositions[i3] = _spawnPosition.x
            tailPositions[i3 + 1] = _spawnPosition.y
            tailPositions[i3 + 2] = _spawnPosition.z

            _velocity.set(
                Math.random() * 2 - 1,
                Math.random() * 2 - 1,
                Math.random() * 2 - 1,
            )

            if (_velocity.lengthSq() < 0.000001) {
                _velocity.set(0, 0, 1)
            }

            _velocity.normalize().multiplyScalar(endCardSparkOutwardVelocity)
            _velocity.x += (Math.random() * 2 - 1) * random
            _velocity.y += (Math.random() * 2 - 1) * random
            _velocity.z += (Math.random() * 2 - 1) * random

            velocities[i3] = _velocity.x
            velocities[i3 + 1] = _velocity.y
            velocities[i3 + 2] = _velocity.z

            const uvOffset = index * 8
            trailUvs[uvOffset] = 0
            trailUvs[uvOffset + 1] = 0
            trailUvs[uvOffset + 2] = 1
            trailUvs[uvOffset + 3] = 0
            trailUvs[uvOffset + 4] = 0
            trailUvs[uvOffset + 5] = 1
            trailUvs[uvOffset + 6] = 1
            trailUvs[uvOffset + 7] = 1

            indices.push(i4, i4 + 2, i4 + 1, i4 + 1, i4 + 2, i4 + 3)
        }

        trailGeometry.setAttribute('uv', new BufferAttribute(trailUvs, 2))
        trailGeometry.setIndex(indices)

        const material = new PointsMaterial({
            color: new Color('#ffffff'),
            alphaMap: circleTexture ?? undefined,
            size: Math.max(0.001, endCardSparkSize),
            transparent: true,
            opacity: Math.max(0, endCardSparkOpacity) * 2.2,
            alphaTest: 0,
            depthWrite: false,
            depthTest: false,
            blending: AdditiveBlending,
            sizeAttenuation: true,
            toneMapped: false,
        })

        const trailMaterial = new MeshBasicMaterial({
            color: new Color(burstColor),
            alphaMap: ribbonTexture ?? undefined,
            transparent: true,
            opacity: Math.max(0, endCardSparkOpacity) * 0.9,
            depthWrite: false,
            depthTest: false,
            blending: AdditiveBlending,
            toneMapped: false,
            side: DoubleSide,
        })

        const trailMesh = new Mesh(trailGeometry, trailMaterial)
        trailMesh.renderOrder = 199
        trailMesh.frustumCulled = false
        rootRef.current?.add(trailMesh)

        const points = new Points(geometry, material)
        points.renderOrder = 200
        points.frustumCulled = false
        rootRef.current?.add(points)

        burstsRef.current.push({
            age: 0,
            lifetime: burstLifetime,
            geometry,
            material,
            points,
            trailGeometry,
            trailMaterial,
            trailMesh,
            positions,
            tailPositions,
            trailVertexPositions,
            velocities,
        })
    }

    useFrame(({ camera }, delta) => {
        const safeDelta = Math.min(delta, 1 / 30)

        for (let index = burstsRef.current.length - 1; index >= 0; index -= 1) {
            const burst = burstsRef.current[index]
            burst.age += safeDelta
            const t = burst.age / burst.lifetime

            if (t >= 1) {
                burst.points.removeFromParent()
                burst.trailMesh.removeFromParent()
                burst.geometry.dispose()
                burst.trailGeometry.dispose()
                burst.material.dispose()
                burst.trailMaterial.dispose()
                burstsRef.current.splice(index, 1)
                continue
            }

            const drag = Math.exp(-endCardSparkDrag * safeDelta)
            const remaining = Math.max(0, 1 - t)
            const lifetimeScale = Math.min(1.75, Math.max(0.2, burst.lifetime / 2))
            const heat = Math.pow(remaining, 2.4)
            const fade = Math.pow(remaining, 1.6)
            const sparkSize = Math.max(
                0.001,
                endCardSparkSize + (endCardSparkEndSize - endCardSparkSize) * t,
            )
            _targetColor.set(burstColor)
            _currentColor.copy(_targetColor).lerp(_hotColor, Math.min(1, heat * 0.95))
            _trailColor.copy(_targetColor).lerp(_hotColor, Math.min(1, heat * 0.55))

            for (let p = 0; p < burst.positions.length; p += 3) {
                burst.positions[p] += burst.velocities[p] * endCardSparkSpeed * safeDelta
                burst.positions[p + 1] += burst.velocities[p + 1] * endCardSparkSpeed * safeDelta
                burst.positions[p + 2] += burst.velocities[p + 2] * endCardSparkSpeed * safeDelta

                burst.velocities[p] *= drag
                burst.velocities[p + 1] = burst.velocities[p + 1] * drag - sparkGravity * safeDelta
                burst.velocities[p + 2] *= drag

                burst.tailPositions[p] += (burst.positions[p] - burst.tailPositions[p]) * trailFollowStrength
                burst.tailPositions[p + 1] += (burst.positions[p + 1] - burst.tailPositions[p + 1]) * trailFollowStrength
                burst.tailPositions[p + 2] += (burst.positions[p + 2] - burst.tailPositions[p + 2]) * trailFollowStrength

                _head.set(burst.positions[p], burst.positions[p + 1], burst.positions[p + 2])
                _tail.set(burst.tailPositions[p], burst.tailPositions[p + 1], burst.tailPositions[p + 2])
                _dir.subVectors(_head, _tail)

                if (_dir.lengthSq() < 0.000001) {
                    _dir.set(0, 0, 0.001)
                } else {
                    _dir.normalize()
                }

                _toCamera.subVectors(camera.position, _head)
                _side.crossVectors(_dir, _toCamera)

                if (_side.lengthSq() < 0.000001) {
                    _side.set(1, 0, 0)
                } else {
                    _side.normalize()
                }

                const particleIndex = p / 3
                const vertexOffset = particleIndex * 12
                const trailWidth = Math.max(0.001, sparkSize * trailWidthScale * (0.2 + remaining * 0.8))
                const trailLength = Math.max(0.002, (sparkSize + _head.distanceTo(_tail) * trailLengthScale) * remaining * lifetimeScale)

                _side.multiplyScalar(trailWidth)
                _tail.addScaledVector(_dir, -trailLength)

                burst.trailVertexPositions[vertexOffset] = _head.x + _side.x
                burst.trailVertexPositions[vertexOffset + 1] = _head.y + _side.y
                burst.trailVertexPositions[vertexOffset + 2] = _head.z + _side.z

                burst.trailVertexPositions[vertexOffset + 3] = _head.x - _side.x
                burst.trailVertexPositions[vertexOffset + 4] = _head.y - _side.y
                burst.trailVertexPositions[vertexOffset + 5] = _head.z - _side.z

                burst.trailVertexPositions[vertexOffset + 6] = _tail.x + _side.x
                burst.trailVertexPositions[vertexOffset + 7] = _tail.y + _side.y
                burst.trailVertexPositions[vertexOffset + 8] = _tail.z + _side.z

                burst.trailVertexPositions[vertexOffset + 9] = _tail.x - _side.x
                burst.trailVertexPositions[vertexOffset + 10] = _tail.y - _side.y
                burst.trailVertexPositions[vertexOffset + 11] = _tail.z - _side.z

                vibrate(50)
            }

            burst.geometry.attributes.position.needsUpdate = true
            burst.trailGeometry.attributes.position.needsUpdate = true
            burst.material.color.copy(_currentColor)
            burst.material.opacity = Math.max(0, endCardSparkOpacity) * (heat * 3.0) * fade
            burst.material.size = sparkSize
            burst.trailMaterial.color.copy(_trailColor)
            burst.trailMaterial.opacity = Math.max(0, endCardSparkOpacity) * (heat * 1.9) * fade
        }
    })

    const emitterPositions = getEmitterPositions()

    return (
        <group ref={rootRef}>
            {endCardSparkShowEmitterDebug
                ? emitterPositions.map((emitter, index) => (
                    <mesh
                        key={index}
                        position={[emitter.x, emitter.y, emitter.z]}
                        renderOrder={250}
                    >
                        <boxGeometry args={[0.24, 0.24, 0.24]} />
                        <meshBasicMaterial
                            color='#00ff99'
                            wireframe
                            toneMapped={false}
                            depthTest={false}
                            transparent
                            opacity={0.95}
                        />
                    </mesh>
                ))
                : null}
        </group>
    )
})




